import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../App';
import { doctorsAPI } from '../utils/api';

const { Title, Text } = Typography;

interface PatientRow {
  patient_id: number;
  full_name: string;
  week_of_pregnancy?: number;
  due_date?: string;
  access_level?: string;
  is_data_hidden_temporarily?: boolean;
  last_activity_at?: string | null;
}

const ACCESS_LABELS: Record<string, string> = {
  ultrasound_only: 'Только УЗИ',
  ultrasound_labs: 'УЗИ и анализы',
  full: 'Весь дневник',
};

export const DoctorsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [patients, setPatients] = useState<PatientRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assignForm] = Form.useForm();
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState<'last_activity' | 'due_date' | 'week_of_pregnancy' | 'full_name'>('last_activity');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [accessLevel, setAccessLevel] = useState<string | undefined>(undefined);
  const [pausedOnly, setPausedOnly] = useState(false);
  const [dueSoonOnly, setDueSoonOnly] = useState(false);

  const getPatientDisplayName = (row: PatientRow) => {
    const name = row.full_name?.trim();
    if (!name || name.startsWith('gAAAA')) {
      return `Пациентка #${row.patient_id}`;
    }
    return name;
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextSort = params.get('sort_by') as 'last_activity' | 'due_date' | 'week_of_pregnancy' | 'full_name' | null;
    const nextOrder = params.get('order') as 'asc' | 'desc' | null;
    const nextAccessLevel = params.get('access_level') || undefined;
    const nextQuery = params.get('q') || '';
    const nextPaused = params.get('paused_only') === '1';
    const nextDueSoon = params.get('due_soon') === '1';

    if (nextSort) setSortBy(nextSort);
    if (nextOrder) setOrder(nextOrder);
    setAccessLevel(nextAccessLevel);
    setQuery(nextQuery);
    setPausedOnly(nextPaused);
    setDueSoonOnly(nextDueSoon);
  }, [location.search]);

  const loadPatients = useCallback(async (customQuery?: string) => {
    try {
      setLoading(true);
      const response = await doctorsAPI.getPatients({
        sort_by: sortBy,
        order,
        access_level: accessLevel,
        paused_only: pausedOnly || undefined,
        q: (customQuery ?? query) || undefined,
      });
      const rawPatients = ((response.data || []) as PatientRow[]).map((row) => ({
        ...row,
        full_name: getPatientDisplayName(row),
      }));
      const filteredPatients = dueSoonOnly
        ? rawPatients.filter((row) => {
            if (!row.due_date) return false;
            const diffDays = dayjs(row.due_date).startOf('day').diff(dayjs().startOf('day'), 'day');
            return diffDays >= 0 && diffDays <= 14;
          })
        : rawPatients;
      setPatients(filteredPatients);
    } catch (error: any) {
      console.error('Error loading doctor patients:', error);
      message.error(error.response?.data?.detail || 'Ошибка загрузки пациенток');
    } finally {
      setLoading(false);
    }
  }, [accessLevel, dueSoonOnly, order, pausedOnly, query, sortBy]);

  useEffect(() => {
    if (user?.role === 'doctor') {
      void loadPatients();
    }
  }, [user, loadPatients]);

  const columns = useMemo(
    () => [
      {
        title: 'Пациентка',
        key: 'patient',
        render: (_: any, row: PatientRow) => (
          <Space direction="vertical" size={2}>
            <Text strong>{getPatientDisplayName(row)}</Text>
            <Space wrap>
              {row.week_of_pregnancy ? <Tag color="blue">{row.week_of_pregnancy} нед.</Tag> : null}
              {row.access_level ? <Tag>{ACCESS_LABELS[row.access_level] || row.access_level}</Tag> : null}
              {row.is_data_hidden_temporarily ? <Tag color="orange">Данные скрыты пациенткой</Tag> : null}
            </Space>
          </Space>
        ),
      },
      {
        title: 'ПДР',
        dataIndex: 'due_date',
        key: 'due_date',
        render: (value: string) => (value ? dayjs(value).format('DD.MM.YYYY') : <Text type="secondary">Не указана</Text>),
      },
      {
        title: 'Последняя активность',
        dataIndex: 'last_activity_at',
        key: 'last_activity_at',
        render: (value: string) => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : <Text type="secondary">Нет данных</Text>),
      },
      {
        title: 'Действия',
        key: 'actions',
        render: (_: any, row: PatientRow) => (
          <Button type="link" onClick={() => navigate(`/doctors/patient/${row.patient_id}`)}>
            Открыть workspace
          </Button>
        ),
      },
    ],
    [navigate]
  );

  const handleSearch = async () => {
    await loadPatients(query);
  };

  const handleAssignPatient = async (values: { patient_email: string }) => {
    try {
      await doctorsAPI.assignPatient(values.patient_email);
      message.success('Пациентка привязана');
      setAssignModalVisible(false);
      assignForm.resetFields();
      await loadPatients();
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Ошибка привязки пациентки');
    }
  };

  if (user?.role === 'admin') {
    return (
      <Card style={{ borderRadius: 16 }}>
        <Title level={3}>Список пациенток врача</Title>
        <Text type="secondary">Для административных операций используйте админ-панель. Рабочее место врача доступно в роли врача.</Text>
      </Card>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ color: '#263238', marginBottom: 8 }}>Пациентки</Title>
        <Text type="secondary">Список наблюдаемых пациенток с понятными статусами доступа и фильтрами врача.</Text>
      </div>

      <Card style={{ borderRadius: 18 }}>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={8}>
            <Input
              allowClear
              placeholder="Поиск по имени"
              prefix={<SearchOutlined />}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>
          <Col xs={24} md={5}>
            <Select
              value={sortBy}
              style={{ width: '100%' }}
              onChange={setSortBy}
              options={[
                { value: 'last_activity', label: 'Сначала по активности' },
                { value: 'due_date', label: 'По ПДР' },
                { value: 'week_of_pregnancy', label: 'По сроку' },
                { value: 'full_name', label: 'По имени' },
              ]}
            />
          </Col>
          <Col xs={24} md={4}>
            <Select
              value={order}
              style={{ width: '100%' }}
              onChange={setOrder}
              options={[
                { value: 'desc', label: 'По убыванию' },
                { value: 'asc', label: 'По возрастанию' },
              ]}
            />
          </Col>
          <Col xs={24} md={4}>
            <Select
              allowClear
              value={accessLevel}
              style={{ width: '100%' }}
              placeholder="Доступ"
              onChange={(value) => setAccessLevel(value)}
              options={Object.entries(ACCESS_LABELS).map(([value, label]) => ({ value, label }))}
            />
          </Col>
          <Col xs={24} md={3}>
            <Select
              value={pausedOnly ? 'paused' : 'all'}
              style={{ width: '100%' }}
              onChange={(value) => setPausedOnly(value === 'paused')}
              options={[
                { value: 'all', label: 'Все' },
                { value: 'paused', label: 'Только скрытые данные' },
              ]}
            />
          </Col>
        </Row>

        {dueSoonOnly ? (
          <div style={{ marginBottom: 16 }}>
            <Tag color="purple">Показаны пациентки с ближайшим ПДР</Tag>
          </div>
        ) : null}

        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Space>
              <Button type="primary" onClick={handleSearch}>Применить фильтры</Button>
              <Button onClick={() => { setQuery(''); setAccessLevel(undefined); setPausedOnly(false); setDueSoonOnly(false); setSortBy('last_activity'); setOrder('desc'); void loadPatients(''); }}>
                Сбросить
              </Button>
            </Space>
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAssignModalVisible(true)}>
              Привязать по email
            </Button>
          </Col>
        </Row>

        {patients.length === 0 && !loading ? (
          <Empty description="Пациентки не найдены" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table
            columns={columns}
            dataSource={patients}
            rowKey="patient_id"
            loading={loading}
            pagination={{ pageSize: 10, showSizeChanger: true }}
          />
        )}
      </Card>

      <Modal
        title="Привязать пациентку по email"
        open={assignModalVisible}
        onCancel={() => {
          setAssignModalVisible(false);
          assignForm.resetFields();
        }}
        footer={null}
      >
        <Form layout="vertical" form={assignForm} onFinish={handleAssignPatient}>
          <Form.Item
            name="patient_email"
            label="Email пациентки"
            rules={[{ required: true, message: 'Укажите email' }, { type: 'email', message: 'Некорректный email' }]}
          >
            <Input placeholder="patient@example.com" />
          </Form.Item>
          <Space>
            <Button onClick={() => setAssignModalVisible(false)}>Отмена</Button>
            <Button type="primary" htmlType="submit">Привязать</Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};
